// frontend_react/src/pages/ProfilePage.jsx

import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  fetchCurrentUser,
  uploadProfilePicture,
} from "../services/authApi";
import logger from "../utils/logger";
import defaultProfilePic from "../assets/profile_picture.jpg";

const resolveAssetUrl = (url) => {
  if (!url) return defaultProfilePic;
  if (url.startsWith("http")) return url;
  return `${window.location.origin}${url}`;
};

export default function ProfilePage() {
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(defaultProfilePic);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadProfile = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await fetchCurrentUser();

      setUser({
        username: data.username || "",
        email: data.email || "",
        role: data.role || "",
        uuid: data.uuid || "",
        firstname: data.firstname || "",
        lastname: data.lastname || "",
        phone_number: data.phone_number || "",
        city: data.city || "",
        company_name: data.company_name || "",
        manager_name: data.manager_name || "Non renseigné",
        job_title: data.job_title || "Non renseigné",
        department: data.department || "Non renseigné",
        contract_type: data.contract_type || "Non renseigné",
        hire_date: data.hire_date || "Non renseignée",
        remaining_leave_days: data.remaining_leave_days ?? 0,
        accrued_leave_days: data.accrued_leave_days ?? 0,
        used_leave_days: data.used_leave_days ?? 0,
      });

      setPhotoPreview(resolveAssetUrl(data.profile_picture_url));
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Impossible de charger le profil."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setMessage("");
    setError("");

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      const { data } = await uploadProfilePicture(formData);

      setPhotoPreview(resolveAssetUrl(data.profile_picture_url));
      setMessage("Photo de profil mise à jour.");

      await loadProfile();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de l’upload de la photo."
      );
    } finally {
      event.target.value = "";
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <PanelInfo>Chargement du profil...</PanelInfo>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <PanelMessage $error>{error}</PanelMessage>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ProfilePanel>
        <HeroCard>
          <AvatarBlock>
            <Avatar src={photoPreview} alt="Photo profil" />
            <AvatarButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              Changer la photo
            </AvatarButton>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </AvatarBlock>

          <HeroInfo>
            <PanelBadge>Profil collaborateur</PanelBadge>
            <h1>
              {user.firstname || user.lastname
                ? `${user.firstname} ${user.lastname}`
                : user.username}
            </h1>
            <p>{user.email}</p>

            <MetaLine>
              <span>{user.role}</span>
              <span>{user.company_name || "Société non renseignée"}</span>
            </MetaLine>
          </HeroInfo>
        </HeroCard>

        {message && <PanelMessage>{message}</PanelMessage>}

        <Grid>
          <Card>
            <h2>Informations du compte</h2>
            <InfoList>
              <InfoRow label="Identifiant" value={user.username} />
              <InfoRow label="E-mail" value={user.email} />
              <InfoRow label="Rôle" value={user.role} />
              <InfoRow
                label="Téléphone"
                value={user.phone_number || "Non renseigné"}
              />
              <InfoRow label="Ville" value={user.city || "Non renseignée"} />
              <InfoRow label="UUID" value={user.uuid} mono />
            </InfoList>
          </Card>

          <Card>
            <h2>Informations RH</h2>
            <InfoList>
              <InfoRow label="Poste" value={user.job_title} />
              <InfoRow label="Service" value={user.department} />
              <InfoRow label="Manager" value={user.manager_name} />
              <InfoRow label="Contrat" value={user.contract_type} />
              <InfoRow label="Date d’entrée" value={user.hire_date} />
            </InfoList>
          </Card>

          <Card>
            <h2>Congés</h2>
            <LeaveGrid>
              <LeaveBox>
                <strong>{user.accrued_leave_days}</strong>
                <span>Cumulés</span>
              </LeaveBox>

              <LeaveBox>
                <strong>{user.used_leave_days}</strong>
                <span>Utilisés</span>
              </LeaveBox>

              <LeaveBox>
                <strong>{user.remaining_leave_days}</strong>
                <span>Restants</span>
              </LeaveBox>
            </LeaveGrid>

            <PrimaryButton type="button">Demander un congé</PrimaryButton>
          </Card>

          <Card>
            <h2>Jours travaillés</h2>
            <WorkTable>
              <thead>
                <tr>
                  <th>Semaine</th>
                  <th>Jours</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Semaine en cours</td>
                  <td>À saisir</td>
                  <td>Brouillon</td>
                </tr>
              </tbody>
            </WorkTable>

            <PrimaryButton type="button">
              Saisir mes jours travaillés
            </PrimaryButton>
          </Card>

          <Card $full>
            <h2>Coffre-fort RH</h2>
            <VaultGrid>
              <VaultItem>
                <strong>Fiches de paie</strong>
                <span>À venir</span>
              </VaultItem>

              <VaultItem>
                <strong>Contrat de travail</strong>
                <span>À venir</span>
              </VaultItem>

              <VaultItem>
                <strong>Documents RH</strong>
                <span>À venir</span>
              </VaultItem>
            </VaultGrid>
          </Card>
        </Grid>
      </ProfilePanel>
    </PageWrapper>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <InfoItem>
      <span>{label}</span>
      <strong className={mono ? "mono" : ""}>
        {value || "Non renseigné"}
      </strong>
    </InfoItem>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  padding: 1.5rem 1.5rem 1.5rem 110px;
  background: #0b1328;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 900px) {
    padding: 1rem;
  }
`;

const ProfilePanel = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  color: #f7f7f7;
`;

const HeroCard = styled.article`
  display: flex;
  gap: 1.4rem;
  align-items: center;
  padding: 1.4rem;
  border-radius: 26px;
  background: #1d263d;
  margin-bottom: 1rem;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const AvatarBlock = styled.div`
  display: grid;
  gap: 0.75rem;
  justify-items: center;
`;

const Avatar = styled.img`
  width: 132px;
  height: 132px;
  object-fit: cover;
  border-radius: 999px;
  border: 4px solid #fff9ea;
`;

const AvatarButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.7rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;
`;

const HeroInfo = styled.div`
  h1 {
    margin: 0.8rem 0 0.4rem;
    font-size: clamp(1.6rem, 4vw, 2.35rem);
    font-weight: 950;
  }

  p {
    margin: 0;
    opacity: 0.78;
  }
`;

const PanelBadge = styled.div`
  display: inline-block;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const MetaLine = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;

  span {
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    background: rgba(255, 249, 234, 0.1);
    color: #fff9ea;
    font-size: 0.82rem;
    font-weight: 850;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
  padding: 1.2rem;
  border-radius: 22px;
  background: #1d263d;
  border: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0 0 1rem;
    font-size: 1.1rem;
    font-weight: 950;
  }
`;

const InfoList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const InfoItem = styled.div`
  display: grid;
  gap: 0.25rem;

  span {
    font-size: 0.76rem;
    opacity: 0.62;
    font-weight: 850;
  }

  strong {
    font-size: 0.95rem;
    font-weight: 850;
    word-break: break-word;
  }

  .mono {
    font-family: monospace;
    font-size: 0.78rem;
    opacity: 0.75;
  }
`;

const LeaveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const LeaveBox = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  text-align: center;

  strong {
    display: block;
    font-size: 1.55rem;
    font-weight: 950;
  }

  span {
    font-size: 0.78rem;
    opacity: 0.72;
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;
`;

const WorkTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  overflow: hidden;
  border-radius: 14px;

  th,
  td {
    padding: 0.8rem;
    text-align: left;
    background: #202a43;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  th {
    font-size: 0.76rem;
    opacity: 0.7;
  }
`;

const VaultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const VaultItem = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    opacity: 0.68;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  padding: 1rem;
  color: #f7f7f7;
  opacity: 0.75;
`;